'use client'

import type { ComponentProps, ReactNode } from 'react'
import type { SourceKey } from '@/components/icons/source-icons'

import { CaretDownIcon, EyeIcon, EyeSlashIcon, PersonSimpleRunIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'
import { SourceIcon } from '@/components/icons/source-icons'

/*
 * GoodUnited / Charts: shared anatomy. Figma: 12 / Charts.
 * Card anatomy follows shadcn; colours and typography remain GoodUnited.
 * Nine approved series, each a vertical gradient from Colors/chart/<series>/start to /end.
 */
export type ChartSeries = 'platform' | 'challenges' | 'facebook' | 'instagram' | 'messenger' | 'birthdays' | 'other' | 'negative' | 'misc'

export const SERIES: Record<ChartSeries, { start: string, end: string, label: 'light' | 'dark' }> = {
    platform: { start: 'var(--gu-chart-platform-start)', end: 'var(--gu-chart-platform-end)', label: 'dark' },
    challenges: { start: 'var(--gu-chart-challenges-start)', end: 'var(--gu-chart-challenges-end)', label: 'dark' },
    facebook: { start: 'var(--gu-chart-facebook-start)', end: 'var(--gu-chart-facebook-end)', label: 'light' },
    instagram: { start: 'var(--gu-chart-instagram-start)', end: 'var(--gu-chart-instagram-end)', label: 'dark' },
    messenger: { start: 'var(--gu-chart-messenger-start)', end: 'var(--gu-chart-messenger-end)', label: 'dark' },
    birthdays: { start: 'var(--gu-chart-birthdays-start)', end: 'var(--gu-chart-birthdays-end)', label: 'dark' },
    other: { start: 'var(--gu-chart-other-start)', end: 'var(--gu-chart-other-end)', label: 'light' },
    negative: { start: 'var(--gu-chart-negative-start)', end: 'var(--gu-chart-negative-end)', label: 'dark' },
    misc: { start: 'var(--gu-chart-misc-start)', end: 'var(--gu-chart-misc-end)', label: 'dark' }
}

export const seriesGradient = (series: ChartSeries) => `linear-gradient(180deg, ${SERIES[series].start}, ${SERIES[series].end})`

/* SVG gradient definitions for every series, id `gu-series-<name>`. Place once inside a chart's <defs>. */
export const SeriesGradients = ({ prefix = 'gu-series' }: { prefix?: string }) => (
    <>
        {(Object.keys(SERIES) as ChartSeries[]).map(series => (
            <linearGradient key={series} id={`${prefix}-${series}`} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={SERIES[series].start} />
                <stop offset='100%' stopColor={SERIES[series].end} />
            </linearGradient>
        ))}
    </>
)

/* Chart card: raised surface, 12px corners, 24px padding (the Layout/Container owns this in blocks). */
export function ChartCard({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='chart-card'
            className={cn('flex flex-col gap-6 rounded-xl border border-border-subtle bg-surface-raised p-6 text-text-primary', className)}
            {...props}
        />
    )
}

interface ChartCardHeaderProps extends Omit<ComponentProps<'div'>, 'title'> {
    title: ReactNode
    description?: ReactNode
    action?: ReactNode
}

/* Chart/CardHeader: Poppins / text-lg / semibold title, Roboto / text-sm description. */
export function ChartCardHeader({ title, description, action, className, ...props }: ChartCardHeaderProps) {
    return (
        <div data-slot='chart-card-header' className={cn('flex items-start justify-between gap-4', className)} {...props}>
            <div className='flex min-w-0 flex-col gap-1'>
                <h3 className='font-heading text-lg font-semibold text-text-primary'>
                    {title}
                </h3>
                {description && (
                    <p className='text-sm text-text-secondary'>
                        {description}
                    </p>
                )}
            </div>
            {action && (
                <div className='shrink-0 text-sm text-text-secondary'>
                    {action}
                </div>
            )}
        </div>
    )
}

interface ChartCardFooterProps extends ComponentProps<'div'> {
    summary: ReactNode
    period?: ReactNode
}

/* Chart/CardFooter: Roboto / text-sm / medium summary and the reporting period. */
export function ChartCardFooter({ summary, period, className, ...props }: ChartCardFooterProps) {
    return (
        <div data-slot='chart-card-footer' className={cn('flex flex-col gap-1 text-sm', className)} {...props}>
            <p className='font-medium text-text-primary'>
                {summary}
            </p>
            {period && (
                <p className='text-text-secondary'>
                    {period}
                </p>
            )}
        </div>
    )
}

/* Chart/Grid: a fixed 16px dot lattice in Colors/chart/grid. Resize the box to crop it. */
export function ChartGrid({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot='chart-grid'
            aria-hidden='true'
            className={cn('h-60 w-full', className)}
            style={{
                backgroundImage: 'radial-gradient(circle, var(--gu-chart-grid) 1.5px, transparent 1.6px)',
                backgroundSize: '16px 16px',
                backgroundPosition: '8px 8px'
            }}
            {...props}
        />
    )
}

/* Chart/AxisLabel: Roboto / text-sm in Colors/chart/axis-text. */
export function AxisLabel({ className, ...props }: ComponentProps<'span'>) {
    return <span data-slot='axis-label' className={cn('text-sm text-chart-axis-text', className)} {...props} />
}

/* Chart/ValueLabel: text placed on a series. tone light sits on dark series, dark on light series. */
export function ValueLabel({ tone = 'dark', className, ...props }: ComponentProps<'span'> & { tone?: 'light' | 'dark' }) {
    return (
        <span
            data-slot='value-label'
            className={cn('text-xs font-medium', tone === 'light' ? 'text-chart-label-light' : 'text-chart-label-dark', className)}
            {...props}
        />
    )
}

/* Chart/SeriesSwatch: 12 x 40, 3px corners. */
export function SeriesSwatch({ series, className, ...props }: ComponentProps<'span'> & { series: ChartSeries }) {
    return (
        <span
            data-slot='series-swatch'
            className={cn('inline-block h-10 w-3 rounded-mark', series === 'other' && 'ring-[0.5px] ring-icon-tertiary ring-inset', className)}
            style={{ backgroundImage: seriesGradient(series) }}
            {...props}
        />
    )
}

/* Chart/LegendLabel: 8px dot and Roboto / text-sm label. */
export function LegendLabel({ series, children, className, ...props }: ComponentProps<'span'> & { series: ChartSeries }) {
    return (
        <span data-slot='legend-label' className={cn('inline-flex items-center gap-2 text-sm text-text-secondary', className)} {...props}>
            <span className='size-2 shrink-0 rounded-full' style={{ backgroundImage: seriesGradient(series) }} aria-hidden='true' />
            {children}
        </span>
    )
}

interface LegendItemProps extends Omit<ComponentProps<'div'>, 'children'> {
    series: ChartSeries
    label: string
    value: string
    share?: string
    /* Shows that source's SourceIcon before the label, so a source keeps the same mark everywhere. */
    source?: SourceKey
    /* Any other icon before the label. Wins over source. */
    icon?: ReactNode
    visible?: boolean
    onToggle?: () => void
}

/* Chart/LegendItem: the same source on the legend and the mark, with an optional icon and an optional show or hide toggle. */
export function LegendItem({ series, label, value, share, source, icon, visible = true, onToggle, className, ...props }: LegendItemProps) {
    const mark = icon ?? (source ? <SourceIcon source={source} /> : null)

    return (
        <div
            data-slot='legend-item'
            data-hidden={!visible || undefined}
            className={cn('flex min-w-0 items-center gap-3 rounded-lg border border-border-subtle bg-surface-raised p-3 transition-opacity data-hidden:opacity-50', className)}
            {...props}
        >
            <span className='w-0.75 self-stretch rounded-full' style={{ backgroundImage: seriesGradient(series) }} aria-hidden='true' />
            <div className='flex min-w-0 flex-1 flex-col'>
                <span className='flex min-w-0 items-center gap-1.5 text-xs text-text-secondary'>
                    {mark && (
                        <span data-slot='legend-icon' className='inline-flex shrink-0 text-icon-primary [&_svg]:size-4' aria-hidden='true'>
                            {mark}
                        </span>
                    )}
                    <span className='truncate'>
                        {label}
                    </span>
                </span>
                <span className='flex items-center gap-1.5 text-base font-medium text-text-primary'>
                    {value}
                    {share && (
                        <span className='rounded-sm bg-surface-muted px-1 text-xs font-medium text-text-secondary'>
                            {share}
                        </span>
                    )}
                </span>
            </div>
            {onToggle && (
                <button
                    type='button'
                    onClick={onToggle}
                    aria-pressed={visible}
                    aria-label={visible ? `Hide ${label}` : `Show ${label}`}
                    className='inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-icon-primary transition-colors outline-none hover:bg-action-secondary-hover focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-4'
                >
                    {visible ? <EyeIcon aria-hidden='true' /> : <EyeSlashIcon aria-hidden='true' />}
                </button>
            )}
        </div>
    )
}

/* Chart/CampaignLabel: an outline pill naming a campaign, with the challenge icon and a caret. */
export function CampaignLabel({ label, className, ...props }: Omit<ComponentProps<'button'>, 'children'> & { label: string }) {
    return (
        <button
            type='button'
            data-slot='campaign-label'
            className={cn('flex h-9 w-full min-w-0 cursor-pointer items-center gap-2 rounded-md border border-border-default bg-surface-raised px-2.5 text-xs text-text-primary transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-border-focus [&_svg]:size-4 [&_svg]:shrink-0', className)}
            {...props}
        >
            <PersonSimpleRunIcon className='text-icon-primary' aria-hidden='true' />
            <span className='min-w-0 flex-1 truncate text-left'>
                {label}
            </span>
            <CaretDownIcon className='text-icon-secondary' aria-hidden='true' />
        </button>
    )
}

/* Chart/CampaignBand: width follows the date range; the band sits behind chart marks. */
export function CampaignBand({ label, className, ...props }: ComponentProps<'div'> & { label: string }) {
    return (
        <div data-slot='campaign-band' className={cn('flex flex-col', className)} {...props}>
            <CampaignLabel label={label} />
            <div className='h-28 rounded-b-md bg-chart-highlight-band' aria-hidden='true' />
        </div>
    )
}

interface StackedTrackSegment {
    series: ChartSeries
    value: number
    icon?: ReactNode
    label: string
}

/* Chart/StackedTrack: normalized shares; the segments always fill 100%. */
export function StackedTrack({ segments, className, showAxis = true }: { segments: StackedTrackSegment[], className?: string, showAxis?: boolean }) {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0) || 1

    return (
        <div data-slot='stacked-track' className={cn('flex flex-col gap-3', className)}>
            <div className='flex h-10 w-full overflow-hidden rounded-md' role='img' aria-label={segments.map(segment => `${segment.label} ${Math.round(segment.value / total * 100)}%`).join(', ')}>
                {segments.map(segment => (
                    <div
                        key={segment.label}
                        className='flex h-full items-center pl-2 text-chart-label-light [&_svg]:size-4'
                        style={{ width: `${segment.value / total * 100}%`, backgroundImage: seriesGradient(segment.series) }}
                    >
                        {segment.icon}
                    </div>
                ))}
            </div>
            {showAxis && (
                <div className='flex justify-between'>
                    <AxisLabel>0%</AxisLabel>
                    <AxisLabel>100%</AxisLabel>
                </div>
            )}
        </div>
    )
}

/* Chart/Sparkline: 112 x 32 trend line in Colors/chart/positive with a soft fill. */
export function Sparkline({ values, className, width = 112, height = 32 }: { values: number[], className?: string, width?: number, height?: number }) {
    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = max - min || 1
    const step = width / Math.max(values.length - 1, 1)
    const points = values.map((value, index) => [index * step, height - 3 - (value - min) / span * (height - 6)])
    const line = points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
    const area = `${line} L${width},${height} L0,${height} Z`

    return (
        <svg data-slot='sparkline' width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={cn('shrink-0 overflow-visible', className)} aria-hidden='true'>
            <path d={area} fill='var(--gu-chart-positive-start)' opacity='0.18' />
            <path d={line} fill='none' stroke='var(--gu-chart-positive-start)' strokeWidth='1.5' strokeLinejoin='round' strokeLinecap='round' />
        </svg>
    )
}
